import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Send, AlertCircle, ShieldAlert } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { track } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AREAS = [
  "Secure enterprise AI", "Generative AI governance", "AI agents", "Sensitive-data protection",
  "AI assurance", "Human oversight", "AI policy enforcement", "Defence supply chain", "Other",
];
const ENVIRONMENTS = [
  "Commercial", "Government", "Defence prime", "Defence supplier", "Research / innovation", "Other",
];

const initial = { name: "", organisation: "", role: "", email: "", area: AREAS[0], environment: ENVIRONMENTS[0], notes: "", consent: false };

export default function BriefingForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.organisation.trim()) e.organisation = "Please enter your organisation.";
    if (!form.role.trim()) e.role = "Please enter your role.";
    if (!form.email.trim()) e.email = "Please enter your work email.";
    else if (!EMAIL_RE.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.consent) e.consent = "Please confirm you consent to us processing your details.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please check the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.name,
        email: form.email,
        company: form.organisation,
        role: form.role,
        enquiry_type: "Defence & National Security Briefing",
        control_area: form.area,
        environment: `Environment: ${form.environment}${form.notes ? `\n\nNotes: ${form.notes}` : ""}`,
        message: `Defence briefing request — ${form.area} — ${form.environment}`,
        consent: form.consent,
      };
      await axios.post(`${API}/demo-requests`, payload);
      track("defence_briefing_submitted", { area: form.area, environment: form.environment });
      navigate(`/thank-you?type=briefing`);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@norse3.com.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-signal-green/30 bg-signal-green/[0.06] p-10 text-center" data-testid="briefing-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-signal-green" />
        <h3 className="mt-5 text-2xl font-bold text-foreground">Briefing request received</h3>
        <p className="mt-3 text-muted-foreground max-w-md mx-auto">
          Thank you. A member of the norse3 team will review your request and respond. For anything urgent, email{" "}
          <a href="mailto:info@norse3.com" className="text-primary hover:underline">info@norse3.com</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8" data-testid="briefing-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Name" required error={errors.name}>
          <Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Jane Doe" className="bg-navy-800 border-white/12" data-testid="briefing-name" />
        </F>
        <F label="Organisation" required error={errors.organisation}>
          <Input value={form.organisation} onChange={(e) => setField("organisation", e.target.value)} placeholder="Organisation" className="bg-navy-800 border-white/12" data-testid="briefing-org" />
        </F>
        <F label="Role" required error={errors.role}>
          <Input value={form.role} onChange={(e) => setField("role", e.target.value)} placeholder="Head of AI Assurance" className="bg-navy-800 border-white/12" data-testid="briefing-role" />
        </F>
        <F label="Work email" required error={errors.email}>
          <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@organisation.gov.uk" className="bg-navy-800 border-white/12" data-testid="briefing-email" />
        </F>
        <F label="Area of interest" required>
          <Select value={form.area} onValueChange={(v) => setField("area", v)}>
            <SelectTrigger className="bg-navy-800 border-white/12" data-testid="briefing-area"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-800 border-white/12">{AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Environment" required>
          <Select value={form.environment} onValueChange={(v) => setField("environment", v)}>
            <SelectTrigger className="bg-navy-800 border-white/12" data-testid="briefing-environment"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-800 border-white/12">{ENVIRONMENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>

      <div className="mt-5">
        <F label="Anything else (optional)">
          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} className="bg-navy-800 border-white/12 resize-none" placeholder="A short, non-sensitive summary of what you'd like to discuss." data-testid="briefing-notes" />
        </F>
        <p className="mt-2 flex items-start gap-2 rounded-lg border border-signal-amber/30 bg-signal-amber/[0.05] px-3.5 py-2.5 text-sm text-signal-amber/90">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          Do not submit classified, operationally sensitive or security-protected information through this form.
        </p>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} className="mt-0.5 border-white/25" data-testid="briefing-consent" />
        <span className="text-sm text-muted-foreground">I consent to norse3 processing my details to respond to this request. <span className="text-signal-red">*</span></span>
      </label>
      {errors.consent && <p className="mt-2 flex items-center gap-1.5 text-sm text-signal-red"><AlertCircle className="h-4 w-4" /> {errors.consent}</p>}

      <button type="submit" disabled={submitting} data-testid="briefing-submit" className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Request briefing</>}
      </button>
    </form>
  );
}

function F({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-sm text-foreground">{label} {required && <span className="text-signal-red">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1.5 flex items-center gap-1.5 text-sm text-signal-red"><AlertCircle className="h-4 w-4" /> {error}</p>}
    </div>
  );
}
